/**
 * Code-family palette — the ONE copy.
 *
 * These are the colors viewers already know from the Dignity classroom lesson
 * ("The Code Families" slide), hospital-profile chargemaster breakdowns and the
 * /hospital-rankings code-type chips. Every code chip, pill, legend swatch or
 * row accent on the site should read its color from here so a CPT is always
 * blue, a DRG always purple, a revenue code always yellow.
 *
 *   CPT #60a5fa · HCPCS #4ade80 · REV #facc15 · DRG #c084fc · NDC #fb7185
 *   SUPPLY #fb923c · CDM / other grey
 *
 * `color` is the canonical swatch (tuned for dark panels). `ink` is a darker
 * shade of the same hue for text on a light background — #facc15 yellow on a
 * cream card is unreadable, #a16207 is the same family and passes contrast.
 */

export type CodeFamilyKey = 'cpt' | 'hcpcs' | 'drg' | 'rev' | 'ndc' | 'supply' | 'other';

export interface CodeFamily {
  key: CodeFamilyKey;
  /** Uppercase token as it appears in data (chargemaster_mappings code slots, rankings columns). */
  type: string;
  /** Short chip label. */
  label: string;
  /** Longer human label for section headers. */
  longLabel: string;
  /** Canonical swatch. */
  color: string;
  /** Text color for the same family on a light background. */
  ink: string;
  /** Route prefix for the per-code page (`${route}/${code}`); empty when there is none. */
  route: string;
  /** One consumer-facing sentence: what kind of thing this family bills. */
  blurb: string;
  /** Example code a consumer might recognise. */
  example: string;
}

export const CODE_FAMILIES: CodeFamily[] = [
  {
    key: 'cpt', type: 'CPT', label: 'CPT', longLabel: 'CPT — procedures, visits, tests & imaging',
    color: '#60a5fa', ink: '#1d4ed8', route: '/cpt',
    blurb: 'The 5-character codes on most outpatient bills: an ER visit, an MRI, a blood panel, a surgery.',
    example: '99283',
  },
  {
    key: 'hcpcs', type: 'HCPCS', label: 'HCPCS', longLabel: 'HCPCS — drugs given in hospital, supplies & other services',
    color: '#4ade80', ink: '#15803d', route: '/hcpcs',
    blurb: 'Letter-plus-4-digit codes for injected drugs, ambulance rides, equipment and services CPT does not cover.',
    example: 'J1885',
  },
  {
    key: 'drg', type: 'DRG', label: 'MS-DRG', longLabel: 'MS-DRG — whole inpatient stays',
    color: '#c084fc', ink: '#7e22ce', route: '/drg',
    blurb: 'One 3-digit code bundles an entire admission: the surgery, the room, the recovery.',
    example: '470',
  },
  {
    key: 'rev', type: 'REV', label: 'Revenue', longLabel: 'Revenue codes — department-level charges',
    color: '#facc15', ink: '#a16207', route: '/rev',
    blurb: 'Four-digit codes for where the charge came from: the ER, the operating room, a night in a room, the pharmacy.',
    example: '0450',
  },
  {
    key: 'ndc', type: 'NDC', label: 'Drug / NDC', longLabel: 'NDC — individual drug products',
    color: '#fb7185', ink: '#be123c', route: '/drugs',
    blurb: 'National Drug Codes name a specific product, maker and package size.',
    example: '00409-4888-10',
  },
  {
    key: 'supply', type: 'SUPPLY', label: 'Supply', longLabel: 'Supply codes — implants, devices & disposables',
    color: '#fb923c', ink: '#c2410c', route: '/supply',
    blurb: 'Hospital-listed supplies: implants, devices, kits and disposables.',
    example: '',
  },
  {
    key: 'other', type: 'CDM', label: 'Other', longLabel: 'Hospital-internal items',
    color: '#9ca3af', ink: '#4b5563', route: '',
    blurb: 'Chargemaster items with no national code — only comparable within the one hospital that lists them.',
    example: '',
  },
];

const BY_KEY: Record<string, CodeFamily> = Object.fromEntries(CODE_FAMILIES.map(f => [f.key, f]));

/** Loose lookup: accepts family keys, data tokens and the usual aliases
 *  ('revenue', 'MS-DRG', 'drug', 'rx', 'cdm', …). Unknown → the grey 'other'. */
export function codeFamily(typeOrKey: string | null | undefined): CodeFamily {
  const t = (typeOrKey || '').toString().trim().toLowerCase();
  switch (t) {
    case 'cpt': return BY_KEY.cpt;
    case 'hcpcs': return BY_KEY.hcpcs;
    case 'drg': case 'ms-drg': case 'msdrg': case 'apr-drg': case 'aprdrg': return BY_KEY.drg;
    case 'rev': case 'revenue': case 'rc': return BY_KEY.rev;
    case 'ndc': case 'drug': case 'drugs': case 'rx': return BY_KEY.ndc;
    case 'supply': case 'sup': case 'supplies': return BY_KEY.supply;
    default: return BY_KEY.other;
  }
}

/** Swatch for a data token — drop-in for the old per-file CODE_TYPE_COLORS maps. */
export function codeFamilyColor(typeOrKey: string | null | undefined): string {
  return codeFamily(typeOrKey).color;
}

/** Uppercase-token → swatch, for callers that index by `row.code_type`. */
export const CODE_TYPE_COLORS: Record<string, string> = {
  CPT: BY_KEY.cpt.color,
  HCPCS: BY_KEY.hcpcs.color,
  DRG: BY_KEY.drg.color,
  'MS-DRG': BY_KEY.drg.color,
  REV: BY_KEY.rev.color,
  REVENUE: BY_KEY.rev.color,
  NDC: BY_KEY.ndc.color,
  DRUG: BY_KEY.ndc.color,
  SUPPLY: BY_KEY.supply.color,
  CDM: BY_KEY.other.color,
  OTHER: BY_KEY.other.color,
};
